'use client';

import { useToast } from '@common/hooks';
import type { SaveWelloCommentResponse } from '@generated/auth';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AuthApi, type COMMENT_TYPE } from '@/api';
import { toContentIdQuery } from '@/api/getCommentList';

interface UsePostCommentMutationParams {
  /** 댓글 API에서 댓글 대상 카테고리로 사용 */
  commentType: COMMENT_TYPE;
  /** 댓글이 달리는 컨텐츠 id */
  contentId: number | string;
  /** 답글일 때 부모 댓글 id */
  parentCommentId?: number;
  /** 답글 트리의 최상위 댓글 id */
  rootCommentId?: number;
  /** 성공 시 상위 composer에 응답을 전달 */
  onSuccess?: (response?: SaveWelloCommentResponse) => void;
}

// 신규 댓글/답글 작성과 관련 쿼리 무효화를 함께 처리한다.
export const usePostCommentMutation = ({
  commentType,
  contentId,
  parentCommentId,
  rootCommentId,
  onSuccess,
}: UsePostCommentMutationParams) => {
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contents: string) =>
      AuthApi['post:/wello/v2/auth/wello-comment']({
        body: {
          commentTypeCd: commentType,
          contents,
          parentWelloCommentId: parentCommentId,
          rootWelloCommentId: rootCommentId,
          ...toContentIdQuery(contentId),
        },
      }),
    onSuccess: async ({ data }) => {
      toast({ type: 'success', message: '댓글 등록완료' });

      // 댓글 작성 후에는 화면별 목록을 따로 구분하지 않고 전체 캐시를 다시 보게 한다.
      await queryClient.invalidateQueries();

      onSuccess?.(data?.context);
    },
    onError: (error) => {
      toastError({ error });
    },
  });
};
